import React, { useEffect, useState } from 'react'
import { youtubeSearchApi } from '@edulastic/api'
import { extractVideoId, isInputElement } from '../../utils/videoPreviewHelpers'
import { StyledYouTubePlayer } from '../../styled-components/VideoPreview'
import appConfig from '../../../../../../app-config'
import { SEEK_DATA, KEYCODES } from '../../constants'

const PLAYER_ID = 'youtube-player'
const TAG_ID = 'iframe_api'
const ED_YOUTUBE_SDK = 'https://www.youtubeeducation.com/iframe_api'
const getYoutubeHost = (isVideoQuizAndAIEnabled) =>
  appConfig.edYouTubePlayerKey && isVideoQuizAndAIEnabled
    ? 'https://www.youtubeeducation.com'
    : 'https://www.youtube.com'

const YouTubePlayer = React.forwardRef(
  (
    {
      url,
      playing,
      onPlay,
      onPause,
      onProgress,
      volume,
      muted,
      progressInterval,
      height,
      width,
      config,
      onEnded,
      onReady,
      onApiChange,
      handleKeyboardSeek,
      isVideoQuizAndAIEnabled,
      isGCPSDistrict,
      onEmbedTokenStatus,
    },
    ref
  ) => {
    const { playerVars, contentFilter } = config?.youtube || {}
    // YouTube's v2 embedded player requires an encrypted "Embedder Identity"
    // token (`embedConfig.enc`) generated server-side. We fetch it before the
    // player is created and re-fetch whenever the video changes.
    const [enc, setEnc] = useState(null)

    const onStateChange = (event) => {
      const player = event.target
      if (event.data === 0) {
        onEnded()
      }

      if (event.data === window.YT.PlayerState.PLAYING) {
        onPlay()
        if (onProgress) {
          const interval = setInterval(() => {
            if (player.getPlayerState() === window.YT.PlayerState.PLAYING) {
              onProgress({ playedSeconds: player.getCurrentTime() })
            } else {
              clearInterval(interval)
            }
          }, progressInterval)
        }
      } else if (event.data === window.YT.PlayerState.PAUSED) {
        if (onPause) {
          onPause()
        }
      }
    }

    const initialize = () => {
      // Extract the video ID from the URL
      const videoId = extractVideoId(url)
      ref.current = new window.YT.Player(PLAYER_ID, {
        videoId,
        height,
        width,
        host: getYoutubeHost(isVideoQuizAndAIEnabled && !isGCPSDistrict), // Disable youtube education for GCPS district
        events: {
          onStateChange,
          onReady,
          onApiChange,
        },
        playerVars,
        embedConfig: { enc },
      })
    }

    const handleKeyDown = (e) => {
      if (ref.current) {
        e = e || window.event
        if (isInputElement(e?.target || document.activeElement)) {
          return true
        }
        const keyCode = window.event ? e.which : e.keyCode
        const isPlaying =
          ref.current?.getPlayerState?.() === window.YT.PlayerState.PLAYING

        if (keyCode == KEYCODES.SPACE) {
          e.preventDefault()
          if (isPlaying) {
            onPause()
          } else {
            onPlay()
          }
          return false
        }

        if (keyCode == KEYCODES.LEFT_ARROW) {
          e.preventDefault()
          handleKeyboardSeek(SEEK_DATA.BACKWARD)
          return false
        }

        if (keyCode == KEYCODES.RIGHT_ARROW) {
          e.preventDefault()
          handleKeyboardSeek(SEEK_DATA.FORWARD)
          return false
        }
      }
    }

    // Fetch a fresh Embedder-Identity token for the current video. Resetting
    // `enc` first forces the player to wait for the new token before re-init.
    useEffect(() => {
      let cancelled = false
      setEnc(null)
      const videoId = extractVideoId(url)
      if (!videoId) {
        return undefined
      }
      // Tell the parent the token is in flight so it can show a loading state
      // instead of a play button the player can't yet honour.
      onEmbedTokenStatus?.('loading')
      youtubeSearchApi
        .fetchEmbedToken({ videoId, contentFilter })
        .then((data) => {
          if (cancelled) {
            return
          }
          const token = data?.enc || null
          setEnc(token)
          onEmbedTokenStatus?.(token ? 'ready' : 'error')
        })
        .catch((error) => {
          // Without a token the v2 player cannot load; log and surface the
          // failure so the UI can show an error instead of a dead play button.
          // eslint-disable-next-line no-console
          console.error('Failed to fetch YouTube embed token', error)
          if (!cancelled) {
            onEmbedTokenStatus?.('error')
          }
        })
      return () => {
        cancelled = true
      }
    }, [url, contentFilter])

    useEffect(() => {
      // Wait for the encrypted token before creating the player.
      if (!enc) {
        return undefined
      }

      if (document.getElementById(TAG_ID) === null) {
        // Load the YouTube iframe API script
        const tag = document.createElement('script')
        tag.id = TAG_ID
        tag.src = ED_YOUTUBE_SDK
        const firstScriptTag = document.getElementsByTagName('script')[0]
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
      } else if (window.YT && window.YT.Player) {
        initialize()
      }

      // Initialize the YouTube player when the API script is loaded
      window.onYouTubeIframeAPIReady = () => {
        initialize()
      }

      document.addEventListener('keydown', handleKeyDown)

      // Clean up the YouTube player when the component is unmounted
      return () => {
        if (ref.current) {
          ref.current?.destroy()
        }
        document.removeEventListener('keydown', handleKeyDown)
      }
    }, [url, enc])

    useEffect(() => {
      if (ref.current) {
        ref.current?.setVolume?.(volume * 100)
        ref.current?.[muted ? 'mute' : 'unMute']?.()
      }
    }, [volume, muted])

    useEffect(() => {
      if (ref.current) {
        if (playing) {
          ref.current?.playVideo?.()
        } else {
          ref.current?.pauseVideo?.()
        }
      }
    }, [playing])

    return (
      <StyledYouTubePlayer>
        <div id={PLAYER_ID} />
      </StyledYouTubePlayer>
    )
  }
)

export default YouTubePlayer
